/**
 * useSchoolReminders — derives KidDayReminder records from the existing
 * checklist API + Compass calendar events.
 *
 * This bridges the current checklist data model (base items, overrides,
 * keyword rules) into the new per-kid section-based reminder model
 * (wear / bring / today).
 *
 * Mapping:
 *  - baseItems / overrides  → section: 'bring'  (general pack items)
 *  - keywordRules matched   → section: 'bring'  (keyword-detected equipment)
 *  - Compass timetable events → section: 'today' (what's on)
 *
 * Source tags:
 *  - baseItems              → source: 'manual'  (parent-configured base)
 *  - overrides              → source: 'manual'
 *  - keywordRules           → source: 'rule'
 *  - Compass events         → source: 'compass'
 *
 * Equipment flags on events (EquipmentFlag[]) become additional 'bring'
 * items tied to the 'rule' source.
 *
 * This hook is intentionally simple and conservative: it never fires
 * network requests of its own — it works entirely from data already
 * fetched by useSchoolChecklist and the parent's eventsByDate.
 */

import { useMemo } from 'react';
import { format } from 'date-fns';
import { useSchoolChecklist } from '../api/hooks';
import type { SchoolChecklistData } from '../api/hooks';
import type { CalendarEvent } from '../types/calendar';
import type { KidDayReminder } from '../types/schoolReminders';

export interface UseSchoolRemindersResult {
  remindersByMember: Record<string, KidDayReminder[]>;
  isLoading: boolean;
}

export function useSchoolReminders(
  date: Date,
  memberIds: string[],
  eventsByMember: Record<string, CalendarEvent[]>,
): UseSchoolRemindersResult {
  const dateStr = format(date, 'yyyy-MM-dd');
  const dayOfWeek = date.getDay();

  const { data: checklistData, isLoading } = useSchoolChecklist(dateStr) as {
    data: SchoolChecklistData | undefined;
    isLoading: boolean;
  };

  const remindersByMember = useMemo<Record<string, KidDayReminder[]>>(() => {
    const result: Record<string, KidDayReminder[]> = {};

    for (const memberId of memberIds) {
      const items: KidDayReminder[] = [];
      const memberEvents = eventsByMember[memberId] ?? [];

      if (checklistData) {
        // Base items → wear or bring section depending on category (shared across all kids)
        for (const baseItem of checklistData.baseItems) {
          if (baseItem.daysOfWeek.length > 0 && !baseItem.daysOfWeek.includes(dayOfWeek)) continue;
          items.push({
            id: `base:${baseItem.id}:${memberId}`,
            memberId,
            date: dateStr,
            section: categorySection(baseItem.category),
            emoji: itemEmoji(baseItem.title, baseItem.category),
            label: baseItem.title,
            detail: baseItem.category ?? 'School item',
            source: 'manual',
          });
        }

        // Overrides → wear or bring section depending on category (per-member overrides)
        for (const override of checklistData.overrides) {
          if (override.memberId !== memberId) continue;
          if (override.daysOfWeek.length > 0 && !override.daysOfWeek.includes(dayOfWeek)) continue;
          items.push({
            id: `override:${override.id}`,
            memberId,
            date: dateStr,
            section: categorySection(override.category),
            emoji: itemEmoji(override.title, override.category),
            label: override.title,
            detail: override.category ?? 'Personal item',
            source: 'manual',
          });
        }

        // Keyword rules → wear or bring section depending on category (auto-detected from event titles)
        for (const rule of checklistData.keywordRules) {
          const matchedEvent = memberEvents.find((e) =>
            e.title.toLowerCase().includes(rule.keyword.toLowerCase()),
          );
          if (!matchedEvent) continue;
          items.push({
            id: `keyword:${rule.id}:${memberId}`,
            memberId,
            date: dateStr,
            section: categorySection(rule.category),
            emoji: itemEmoji(rule.checklistItem, rule.category),
            label: rule.checklistItem,
            detail: `Auto-detected from: ${matchedEvent.title}`,
            source: 'rule',
          });
        }
      }

      // Equipment flags on events → wear or bring section depending on category (rule source)
      for (const event of memberEvents) {
        if (!event.equipmentFlags?.length) continue;
        for (const flag of event.equipmentFlags) {
          items.push({
            id: `equip:${event.id}:${flag.keyword}:${memberId}`,
            memberId,
            date: dateStr,
            section: categorySection(flag.category),
            emoji: itemEmoji(flag.item, flag.category),
            label: flag.item,
            detail: `For: ${event.title}`,
            source: 'rule',
            sourceRef: event.id,
          });
        }
      }

      // Compass/ICS timetable events → today section
      for (const event of memberEvents) {
        if (event.sourceType !== 'compass' && event.sourceType !== 'ics') continue;
        const startTime = event.allDay ? undefined : formatEventTime(event.startTime);
        items.push({
          id: `event:${event.id}:${memberId}`,
          memberId,
          date: dateStr,
          section: 'today',
          emoji: '📅',
          label: event.title,
          detail: event.location ?? event.description ?? '',
          time: startTime,
          source: 'compass',
          sourceRef: event.id,
        });
      }

      // Dedup: keyword-rule rows and equipment-flag rows can describe the same
      // logical item (both derived from the same schoolKeywordRule). Keep the
      // first occurrence (keyword-rule rows are pushed before equipment-flag
      // rows, so "Auto-detected from:" wins over "For:"). Never collapse 'today'
      // rows — those are real distinct events.
      const seen = new Set<string>();
      const deduped = items.filter((it) => {
        if (it.section === 'today') return true;
        const key = `${it.section}|${it.label.trim().toLowerCase()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      result[memberId] = deduped;
    }

    return result;
  }, [checklistData, memberIds, eventsByMember, dateStr, dayOfWeek]);

  return { remindersByMember, isLoading };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determine the reminder section from a category string.
 * Items related to clothing / uniform / sport gear → 'wear'.
 * Everything else → 'bring'.
 *
 * This lets keyword rules and equipment flags reach the Wear section
 * (e.g. "PE uniform" from a Sport subject rule) rather than always
 * landing in Bring.
 */
function categorySection(category: string | null): 'wear' | 'bring' {
  if (!category) return 'bring';
  const c = category.toLowerCase();
  if (
    c.includes('uniform') ||
    c.includes('wear') ||
    c.includes('clothes') ||
    c.includes('dress') ||
    c.includes('hat') ||
    c.includes('shoe') ||
    c.includes('kit') ||
    c.includes('costume') ||
    c.includes('outfit')
  ) {
    return 'wear';
  }
  return 'bring';
}

function categoryEmoji(category: string | null): string {
  if (!category) return '📦';
  const c = category.toLowerCase();
  if (c.includes('sport') || c.includes('pe') || c.includes('gym')) return '⚽';
  if (c.includes('music') || c.includes('instrument')) return '🎵';
  if (c.includes('art')) return '🎨';
  if (c.includes('food') || c.includes('lunch')) return '🥪';
  if (c.includes('book') || c.includes('library')) return '📕';
  if (c.includes('tech') || c.includes('laptop') || c.includes('device')) return '💻';
  if (c.includes('swim')) return '🏊';
  if (c.includes('uniform') || c.includes('hat') || c.includes('kit')) return '👕';
  return '🎒';
}

/**
 * Derive a display emoji from the item label first, then fall back to
 * categoryEmoji(category). This fixes the "all Bring items show 🎒"
 * problem for base items (Brush teeth, Water bottle, etc.) whose
 * category is null/empty and therefore always hit the 🎒 default.
 */
function itemEmoji(label: string, category: string | null): string {
  const l = label.toLowerCase();
  // hair before brush: "Hair brush" must not match the toothbrush rule.
  if (l.includes('hair')) return '💈';
  if (l.includes('tooth') || l.includes('teeth') || l.includes('brush')) return '🪥';
  if (l.includes('water') || l.includes('bottle')) return '💧';
  if (l.includes('lunch')) return '🥪';
  if (l.includes('hat') || l.includes('cap')) return '🧢';
  if (l.includes('charge') || l.includes('laptop') || l.includes('phone')) return '💻';
  if (l.includes('shoe') || l.includes('boot')) return '👟';
  if (l.includes('swim') || l.includes('togs')) return '🩱';
  if (l.includes('library') || l.includes('book')) return '📚';
  if (l.includes('sunscreen')) return '🧴';
  return categoryEmoji(category);
}

function formatEventTime(isoTime: string): string | undefined {
  try {
    const d = new Date(isoTime);
    return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return undefined;
  }
}
